if (!customElements.get('slideshow-new-video')) {
  const YOUTUBE_ORIGIN = 'https://www.youtube-nocookie.com';
  const MOBILE_QUERY = '(max-width: 749px)';

  customElements.define(
    'slideshow-new-video',
    class SlideshowNewVideo extends HTMLElement {
      connectedCallback() {
        this.slots = Array.from(this.querySelectorAll('[data-slideshow-video-slot]')).map((el) => ({
          el,
          media: el.dataset.slideshowVideoSlot,
          type: el.dataset.slideshowYoutube ? 'youtube' : 'file',
          playing: false
        }));

        this.toggle = this.querySelector('[data-slideshow-video-toggle]');
        if (!this.slots.length) {
          if (this.toggle) this.toggle.hidden = true;
          return;
        }

        this.slide = this.closest('.slideshow-new__slide');
        this.userPaused = false;
        this.userStarted = false;
        this.inView = false;

        if (this.toggle) this.toggle.addEventListener('click', this.toggleVideo.bind(this));
        this.slots.filter((slot) => slot.type === 'file').forEach((slot) => this.setupFileSlot(slot));

        this.onMessage = (event) => {
          if (event.origin !== YOUTUBE_ORIGIN) return;
          const slot = this.slots.find((item) => item.iframe && event.source === item.iframe.contentWindow);
          if (!slot) return;
          let data;
          try {
            data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          } catch (error) {
            return;
          }
          if (data && data.info && data.info.playerState === 1 && slot === this.activeSlot) this.classList.add('is-playing');
        };
        window.addEventListener('message', this.onMessage);

        this.mobileQuery = window.matchMedia(MOBILE_QUERY);
        this.onMediaChange = () => {
          this.selectSlot();
          this.update();
        };
        if (this.mobileQuery.addEventListener) this.mobileQuery.addEventListener('change', this.onMediaChange);
        else this.mobileQuery.addListener(this.onMediaChange);
        this.selectSlot();

        if (this.slide) {
          this.classObserver = new MutationObserver(() => this.update());
          this.classObserver.observe(this.slide, { attributes: true, attributeFilter: ['class'] });
        }

        if ('IntersectionObserver' in window) {
          this.viewObserver = new IntersectionObserver(
            (entries) => {
              this.inView = entries[0].isIntersecting;
              this.update();
            },
            { threshold: 0.25 }
          );
          this.viewObserver.observe(this);
        } else {
          this.inView = true;
          this.update();
        }

        this.onVisibilityChange = () => this.update();
        document.addEventListener('visibilitychange', this.onVisibilityChange);
      }

      disconnectedCallback() {
        if (this.classObserver) this.classObserver.disconnect();
        if (this.viewObserver) this.viewObserver.disconnect();
        if (this.onVisibilityChange) document.removeEventListener('visibilitychange', this.onVisibilityChange);
        if (this.onMessage) window.removeEventListener('message', this.onMessage);
        if (this.mobileQuery && this.onMediaChange) {
          if (this.mobileQuery.removeEventListener) this.mobileQuery.removeEventListener('change', this.onMediaChange);
          else this.mobileQuery.removeListener(this.onMediaChange);
        }
        if (this.slots) this.slots.forEach((slot) => clearTimeout(slot.revealTimer));
      }

      selectSlot() {
        const wanted = this.mobileQuery.matches ? 'mobile' : 'desktop';
        const next = this.slots.find((slot) => slot.media === wanted || slot.media === 'all') || null;
        if (next === this.activeSlot) return this.reflectState();

        if (this.activeSlot) this.pausePlayer(this.activeSlot);
        this.activeSlot = next;

        this.classList.remove('is-playing');
        this.reflectState();
      }

      /* ---------------- Fichier MP4 ---------------- */

      setupFileSlot(slot) {
        const video = slot.el;
        video.addEventListener('playing', () => {
          if (slot === this.activeSlot) this.classList.add('is-playing');
          this.reflectState();
        });
        video.addEventListener('pause', () => this.reflectState());

     
        const onError = () => {
          slot.failed = true;
          if (slot === this.activeSlot) this.classList.remove('is-playing');
          this.reflectState();
        };
        video.addEventListener('error', onError);
        video.querySelectorAll('source').forEach((source) => source.addEventListener('error', onError));
      }

      loadFileSlot(slot) {
        if (slot.loaded) return;
        slot.loaded = true;
        slot.el.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        slot.el.load();
      }

      /* ---------------- YouTube ---------------- */

      createYoutubePlayer(slot) {
        const id = slot.el.dataset.slideshowYoutube;
        const params = new URLSearchParams({
          autoplay: '1',
          mute: '1',
          loop: '1',
          playlist: id,
          controls: '0',
          disablekb: '1',
          fs: '0',
          iv_load_policy: '3',
          modestbranding: '1',
          playsinline: '1',
          rel: '0',
          enablejsapi: '1',
          origin: window.location.origin
        });

        const iframe = document.createElement('iframe');
        iframe.src = YOUTUBE_ORIGIN + '/embed/' + encodeURIComponent(id) + '?' + params.toString();
        iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
        iframe.setAttribute('tabindex', '-1');
        iframe.setAttribute('title', '');
        slot.iframe = iframe;

        iframe.addEventListener('load', () => {
          this.sendYoutube(slot, { event: 'listening', id: 1, channel: 'widget' });
         
          slot.revealTimer = setTimeout(() => {
            if (slot.playing && slot === this.activeSlot) this.classList.add('is-playing');
          }, 3000);
        });

        slot.el.appendChild(iframe);
      }

      sendYoutube(slot, message) {
        if (!slot.iframe || !slot.iframe.contentWindow) return;
        slot.iframe.contentWindow.postMessage(JSON.stringify(message), YOUTUBE_ORIGIN);
      }

      /* ---------------- Lecture ---------------- */

      isPaused() {
        const slot = this.activeSlot;
        if (!slot) return true;
        return slot.type === 'file' ? slot.el.paused : !slot.playing;
      }

      playPlayer() {
        const slot = this.activeSlot;
        if (slot.type === 'file') {
          this.loadFileSlot(slot);
          const played = slot.el.play();
          if (played && played.catch) played.catch(() => this.reflectState());
          return;
        }
        if (!slot.iframe) this.createYoutubePlayer(slot);
        else this.sendYoutube(slot, { event: 'command', func: 'playVideo', args: [] });
        slot.playing = true;
      }

      pausePlayer(slot) {
        if (!slot) return;
        if (slot.type === 'file') {
          if (!slot.el.paused) slot.el.pause();
          return;
        }
        if (slot.iframe && slot.playing) this.sendYoutube(slot, { event: 'command', func: 'pauseVideo', args: [] });
        slot.playing = false;
      }

      saveDataEnabled() {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        return Boolean(connection && (connection.saveData || /2g/.test(connection.effectiveType || '')));
      }

      reducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      lowPowerMode() {
        if (typeof this.lowPower === 'boolean') return Promise.resolve(this.lowPower);
        if (!navigator.getBattery) {
          this.lowPower = false;
          return Promise.resolve(false);
        }
        return navigator
          .getBattery()
          .then((battery) => {
            this.lowPower = !battery.charging && battery.level <= 0.2;
            return this.lowPower;
          })
          .catch(() => {
            this.lowPower = false;
            return false;
          });
      }

      autoplayAllowed() {
        if (this.userStarted) return Promise.resolve(true);
        if (this.dataset.autoplay !== 'true' || this.reducedMotion() || this.saveDataEnabled()) {
          return Promise.resolve(false);
        }
        return this.lowPowerMode().then((lowPower) => !lowPower);
      }

      isActive() {
        return !this.slide || this.slide.classList.contains('slideshow-new__slide--active');
      }

      canPlay() {
        return Boolean(this.activeSlot) && !this.activeSlot.failed && this.isActive() && this.inView && !document.hidden && !this.userPaused;
      }

      update() {
        if (!this.canPlay()) {
          this.pausePlayer(this.activeSlot);
          this.reflectState();
          return;
        }

        this.autoplayAllowed().then((allowed) => {
          
          if (!allowed || !this.canPlay()) return this.reflectState();
          this.playPlayer();
          this.reflectState();
        });
      }

      toggleVideo() {
        if (this.isPaused()) {
          this.userPaused = false;
          this.userStarted = true;
          this.update();
        } else {
          this.userPaused = true;
          this.pausePlayer(this.activeSlot);
        }
        this.reflectState();
      }

      reflectState() {
        if (!this.toggle) return;
        
        this.toggle.hidden = !this.activeSlot || Boolean(this.activeSlot.failed);
        const paused = this.isPaused();
        const pauseIcon = this.toggle.querySelector('.slideshow-new__video-icon-pause');
        const playIcon = this.toggle.querySelector('.slideshow-new__video-icon-play');
        if (pauseIcon) pauseIcon.toggleAttribute('hidden', paused);
        if (playIcon) playIcon.toggleAttribute('hidden', !paused);
        this.toggle.setAttribute('aria-label', paused ? this.dataset.playLabel : this.dataset.pauseLabel);
      }
    }
  );
}
